/**
 * QRScannerInput - USB scanner input field
 *
 * Key behaviors:
 * - Auto-focus on mount and after each action
 * - Submit on Enter (USB scanners send Enter after scan)
 * - Clear input after processing
 * - Large input for warehouse visibility
 * - Audio beep on scan (optional, default enabled)
 */
import { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Loader2, QrCode } from 'lucide-react';
import { playSuccessBeep, playErrorBeep } from '@/lib/sounds';
import { isThpPayloadComplete } from '@/lib/qr-utils';

interface QRScannerInputProps {
  onScan: (qrCode: string) => void;
  /** Called when an invalid QR code is scanned (optional - error sound plays automatically) */
  onInvalidQR?: (message: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  autoFocus?: boolean;
  /** Play beep sound when scan is captured (default: true) */
  soundEnabled?: boolean;
  /** Override the default aria-label for contextual usage */
  ariaLabel?: string;
  /** Override the default placeholder text for contextual usage (default: "Scan garment QR code...") */
  placeholder?: string;
  /**
   * FRONTEND-4N: keep the input focusable while `disabled`/`isLoading` instead of
   * setting the HTML `disabled` attribute. A disabled input cannot hold focus, so
   * during a busy window (garment lookup, print in flight) scanner keystrokes
   * drift to `document.body` — or the browser URL bar — and a scan becomes a
   * `/production/printerTHP:/…` 404. With this flag the field stays the focus
   * target, buffers the scan, and submits it as soon as the station is free
   * (key-ahead). Off by default — other stations keep the disabled-attr behavior.
   */
  keepFocusWhenBusy?: boolean;
}

/** Maximum length for QR code payload (prevents DoS with huge strings) */
const MAX_QR_LENGTH = 2048;

/**
 * Grace for a long scan's trailing characters to land before it's treated as
 * truncated. A keyboard-wedge scanner can fire its Enter suffix before the tail
 * of a ~142-char `THP://<base64>` garment QR has committed to the input; without
 * this, the truncated value is submitted and the backend throws
 * "Invalid QR code: failed to parse JSON". Only THP base64 payloads that don't
 * yet decode ever wait — complete scans (incl. short `THP://BIN:` codes) submit
 * with zero added latency. ~500ms total budget.
 */
const SCAN_SETTLE_MS = 50;
/**
 * Give up on an incomplete scan only after the field has stopped growing for this
 * many consecutive ticks (~300ms of no new characters) — i.e. the scanner has
 * actually stopped. Crucially NOT a fixed total budget: a warehouse scanner with
 * an inter-character delay can take well over a second to stream a 142-char code,
 * and a fixed timeout would fire *mid-scan*, clear the field, and dump the tail
 * into an empty box (the exact "clears then keeps typing into nothing" failure).
 */
const SCAN_QUIET_TICKS = 6;
/** Absolute safety cap (~10s) so a pathological stream can't loop forever. */
const SCAN_ABS_MAX_TICKS = 200;

export function QRScannerInput({
  onScan,
  onInvalidQR,
  disabled = false,
  isLoading = false,
  autoFocus = true,
  soundEnabled = true,
  ariaLabel,
  placeholder,
  keepFocusWhenBusy = false,
}: QRScannerInputProps) {
  const { t } = useTranslation('production');
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedPlaceholder = placeholder ?? t('scanGarmentPlaceholder');

  // Pending "wait for the scan tail" timer + attempt counter (see finalizeScan).
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleAttemptsRef = useRef(0);
  // Longest value seen this scan + ticks since it last grew — used to tell a
  // still-streaming (slow) scanner apart from a genuinely truncated one.
  const settleMaxLenRef = useRef(0);
  const settleQuietTicksRef = useRef(0);

  // Auto-focus on mount and when re-enabled
  useEffect(() => {
    if (!disabled && autoFocus) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [disabled, autoFocus]);

  // Re-focus after loading completes
  useEffect(() => {
    if (!isLoading && !disabled) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [isLoading, disabled]);

  /**
   * Validate QR code format before sending to handler.
   * Returns error message if invalid, null if valid.
   */
  const validateQRCode = useCallback((value: string): string | null => {
    // Check for length limit (prevent memory issues with huge payloads)
    if (value.length > MAX_QR_LENGTH) {
      return t('qr.tooLong');
    }

    // Check for THP:// prefix (ThreadHeads protocol)
    if (!value.startsWith('THP://')) {
      return t('qr.invalidFormat');
    }

    return null;
  }, [t]);

  const processQRCode = useCallback((value: string) => {
    // Validate QR code format
    const error = validateQRCode(value);
    if (error) {
      // Play error sound for invalid QR
      if (soundEnabled) {
        playErrorBeep();
      }
      // Notify parent if callback provided
      onInvalidQR?.(error);
      return;
    }

    // Valid QR - play success and send to handler
    if (soundEnabled) {
      playSuccessBeep();
    }
    onScan(value);
  }, [validateQRCode, onScan, onInvalidQR, soundEnabled]);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    settleAttemptsRef.current = 0;
    settleMaxLenRef.current = 0;
    settleQuietTicksRef.current = 0;
  }, []);

  /**
   * Submit the current scan — but never a half-arrived one, and never clear the
   * field while characters are still streaming in.
   *
   * A keyboard-wedge scanner can trigger Enter (its own suffix, or an impatient
   * packer) before a long `THP://<base64>` garment QR has finished arriving, and
   * many warehouse scanners stream with a per-character delay that takes well over
   * a second. So on an incomplete scan we hold and re-read, and we only give up
   * once the field has stopped GROWING for a short quiet window — meaning the
   * scanner has actually stopped, not merely that a fixed timeout elapsed. Giving
   * up on a fixed timeout would clear the box mid-scan and dump the tail into an
   * empty field. Complete scans submit immediately (zero added latency).
   */
  const finalizeScan = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    // Busy: in key-ahead mode leave the buffered value in the field — the
    // busy-transition effect below finalizes it once the station is free.
    if (disabled || isLoading) return;

    const value = el.value.trim();
    if (!value) {
      clearSettleTimer();
      return;
    }

    if (!isThpPayloadComplete(value)) {
      // Track growth: as long as the field keeps getting longer, the scan is still
      // arriving — keep waiting. Only the quiet window (no growth) counts down.
      if (value.length > settleMaxLenRef.current) {
        settleMaxLenRef.current = value.length;
        settleQuietTicksRef.current = 0;
      } else {
        settleQuietTicksRef.current += 1;
      }
      settleAttemptsRef.current += 1;

      if (
        settleQuietTicksRef.current < SCAN_QUIET_TICKS &&
        settleAttemptsRef.current < SCAN_ABS_MAX_TICKS
      ) {
        // Keep a single settle chain even if Enter is pressed repeatedly.
        if (settleTimerRef.current !== null) clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(finalizeScan, SCAN_SETTLE_MS);
        return;
      }
      // The scanner stopped while the payload is still incomplete — the tail is
      // genuinely lost. Now it's safe to clear (nothing more is coming) and flag
      // it, rather than round-tripping a known-bad payload to the backend.
      clearSettleTimer();
      el.value = '';
      if (soundEnabled) {
        playErrorBeep();
      }
      onInvalidQR?.(t('qr.incompleteScan'));
      return;
    }

    clearSettleTimer();
    el.value = '';
    processQRCode(value);
  }, [disabled, isLoading, clearSettleTimer, processQRCode, soundEnabled, onInvalidQR, t]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    finalizeScan();
  }, [finalizeScan]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // USB scanners typically send Enter after the scan
    if (e.key === 'Enter') {
      e.preventDefault();
      finalizeScan();
    }
  }, [finalizeScan]);

  // Cancel any pending settle when the field is disabled/busy or unmounts, so we
  // never re-read a stale value or leak a timer.
  useEffect(() => {
    if (disabled || isLoading) clearSettleTimer();
  }, [disabled, isLoading, clearSettleTimer]);

  // Key-ahead: a scan buffered while busy submits as soon as the station frees
  // up (also prevents a stale tail from corrupting the NEXT scan). Without
  // keepFocusWhenBusy the input was HTML-disabled while busy, so the field is
  // always empty here and this is a no-op.
  useEffect(() => {
    if (keepFocusWhenBusy && !disabled && !isLoading && inputRef.current?.value.trim()) {
      finalizeScan();
    }
  }, [keepFocusWhenBusy, disabled, isLoading, finalizeScan]);

  useEffect(() => () => clearSettleTimer(), [clearSettleTimer]);

  return (
    <form
      onSubmit={handleSubmit}
      className="relative"
      role="search"
      aria-label={ariaLabel || t('scanGarmentQr')}
    >
      <div className="relative">
        <QrCode
          className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          data-scanner-input
          ref={inputRef}
          placeholder={resolvedPlaceholder}
          // keepFocusWhenBusy: never set the HTML disabled attr (a disabled input
          // can't hold focus, so scans would leak to body/URL) — busy scans are
          // buffered by finalizeScan instead. aria-busy still reflects loading.
          disabled={keepFocusWhenBusy ? false : disabled || isLoading}
          className="h-16 text-lg pl-12 pr-12 font-mono"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          // PRODUCTION_DESIGN §12.1: scanner-first input — suppress the on-screen
          // keyboard on touch devices (hardware USB scanner is the input source).
          inputMode="none"
          onKeyDown={handleKeyDown}
          aria-label={ariaLabel || t('qr.scannerAriaLabel')}
          aria-busy={isLoading}
          aria-describedby={isLoading ? "scanner-loading" : undefined}
        />
        {isLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Loader2
              className="h-5 w-5 animate-spin text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        )}
        {isLoading && (
          <span id="scanner-loading" className="sr-only">
            {t('processingDots')}
          </span>
        )}
      </div>
    </form>
  );
}
